import {
  NOTIFICATION_TRIGGERS,
  type NotificationTriggerType,
} from '@cims-online/shared'
import { Form } from 'antd'
import CIMSFormItem from 'components/CIMSFormItem'
import CIMSSelect from 'components/CIMSSelect'
import { useOptions } from 'components/CIMSSelect/hooks/useOptions'
import { triggerMetadata } from '../../../../../../utils/triggerMetadata'
import type { NotificationField, NotificationStatus } from '../../../../types'
import SettingsCard from '../SettingsCard'
import TriggerTypeSettings from './components/TriggerTypeSettings'

type TriggerSettingsProps = {
  fields: NotificationField[]
  statuses: NotificationStatus[]
}

function isTrigger(value: unknown): value is NotificationTriggerType {
  return (
    typeof value === 'string' &&
    NOTIFICATION_TRIGGERS.some((trigger) => trigger === value)
  )
}

function capitalizeFirst(value: string) {
  return `${value[0]?.toUpperCase() ?? ''}${value.slice(1)}`
}

/**
 * Renders the trigger-specific controls within the notification form.
 *
 * The component must be rendered within an Ant Form. Trigger drafts remain in
 * the parent form state when their controls are hidden, allowing an admin to
 * switch triggers without losing unsaved values.
 */
function TriggerSettings({ fields, statuses }: TriggerSettingsProps) {
  const triggerOptions = useOptions({
    items: NOTIFICATION_TRIGGERS.map((trigger) => {
      const { clause, description } = triggerMetadata(trigger)
      return {
        label: capitalizeFirst(clause),
        tooltip: description,
        value: trigger,
      }
    }),
    label: 'label',
    sort: false,
    tooltip: 'tooltip',
    value: 'value',
  })

  return (
    <SettingsCard>
      <CIMSFormItem
        label="When"
        name="trigger"
        requiredMessage="Please select a notification trigger"
      >
        <CIMSSelect
          simple
          options={triggerOptions}
          placeholder="Select a trigger"
        />
      </CIMSFormItem>
      <Form.Item
        noStyle
        shouldUpdate={(before, after) => before.trigger !== after.trigger}
      >
        {({ getFieldValue }) => {
          const trigger = getFieldValue('trigger') as unknown
          return isTrigger(trigger) ? (
            <TriggerTypeSettings
              fields={fields}
              statuses={statuses}
              trigger={trigger}
            />
          ) : null
        }}
      </Form.Item>
    </SettingsCard>
  )
}

export default TriggerSettings
